import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as XLSX from 'xlsx'
import moment from 'moment'
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CButton,
  CLoadingButton,
  CFormInput,
  CFormSelect,
  CAlert,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CSpinner,
  CBadge,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilArrowLeft,
  cilCheck,
  cilCheckAlt,
  cilCheckCircle,
  cilCloudDownload,
  cilCloudUpload,
  cilFile,
  cilInfo,
  cilWarning,
  cilX,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import FixtureService from 'src/service/FixtureService'
import TeamService from 'src/service/TeamService'
import RoundService from 'src/service/RoundService'
import CommonService from 'src/service/CommonService'
import {
  DATE_ORDER_LABELS,
  detectDateOrder,
  displayDate,
  displayTime,
  fromExcelSerial,
  normalizeDateField,
  normalizeTimeField,
} from 'src/utils/importDateTime'
import ExampleSingle from '../../assets/FixtureSampleFile.csv'
import ExampleDual from '../../assets/FixtureSampleFileIndoor.csv'

const WIZARD_STEPS = ['Upload File', 'Validate Fixtures', 'Review & Import']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

// The backend hardcodes these two match types (ids 27/28) regardless of what's in the
// dropdown_managers table, and checks case-insensitively - so this is what actually decides
// validity. matchTypeOptions (fetched) only supplies the display labels for the edit dropdown.
const MATCH_TYPE_SET = new Set(['1-day', '2-day'])
const FALLBACK_MATCH_TYPES = ['1-Day', '2-Day']

const RESULT_META = {
  valid: { label: 'Valid', color: 'success', icon: cilCheckCircle, accent: '#198754' },
  missing_team1: {
    label: (mode) => (mode === 'single' ? 'Missing Team Code' : 'Missing Team 1'),
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  invalid_team1: {
    label: (mode) => (mode === 'single' ? 'Invalid Team Code' : 'Invalid Team 1'),
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  missing_team2: { label: 'Missing Team 2', color: 'danger', icon: cilWarning, accent: '#dc3545' },
  invalid_team2: { label: 'Invalid Team 2', color: 'danger', icon: cilWarning, accent: '#dc3545' },
  missing_match_type: {
    label: 'Missing Match Type',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  invalid_match_type: {
    label: 'Invalid Match Type',
    color: 'warning',
    icon: cilWarning,
    accent: '#ffc107',
  },
  missing_start: {
    label: 'Missing Start Date/Time',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  missing_end: {
    label: 'Missing End Date/Time',
    color: 'danger',
    icon: cilWarning,
    accent: '#dc3545',
  },
  invalid_start: {
    label: 'Invalid Start Date/Time',
    color: 'warning',
    icon: cilWarning,
    accent: '#ffc107',
  },
  invalid_end: {
    label: 'Invalid End Date/Time',
    color: 'warning',
    icon: cilWarning,
    accent: '#ffc107',
  },
  invalid_range: {
    label: 'End Before Start',
    color: 'warning',
    icon: cilWarning,
    accent: '#ffc107',
  },
  no_round: {
    label: 'No Matching Round',
    color: 'info',
    icon: cilWarning,
    accent: '#0dcaf0',
  },
}

const tintStyle = (hex, alphaHex) => ({ backgroundColor: `${hex}${alphaHex}` })

const guessColumn = (headers, patterns) =>
  headers.find((h) =>
    patterns.some((p) =>
      String(h)
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .includes(p),
    ),
  ) || ''

const buildRow = (base, ctx) => {
  const { mode, teamBySn, roundWindows, dateOrder } = ctx
  const team1 = String(base.team1 ?? '').trim()
  const team2 = mode === 'dual' ? String(base.team2 ?? '').trim() : ''
  const matchType = String(base.match_type ?? '').trim()
  const sd = normalizeDateField(base.start_date, dateOrder)
  const st = normalizeTimeField(base.start_time)
  const ed = normalizeDateField(base.end_date, dateOrder)
  const et = normalizeTimeField(base.end_time)

  let result = null
  if (!team1) result = 'missing_team1'
  else if (!teamBySn.has(team1.toUpperCase())) result = 'invalid_team1'
  else if (mode === 'dual' && !team2) result = 'missing_team2'
  else if (mode === 'dual' && !teamBySn.has(team2.toUpperCase())) result = 'invalid_team2'
  else if (!matchType) result = 'missing_match_type'
  else if (!MATCH_TYPE_SET.has(matchType.toLowerCase())) result = 'invalid_match_type'
  else if (!sd.text || !st.text) result = 'missing_start'
  else if (!ed.text || !et.text) result = 'missing_end'
  else if (!sd.valid || !st.valid) result = 'invalid_start'
  else if (!ed.valid || !et.valid) result = 'invalid_end'

  let startDT = null
  let endDT = null
  if (sd.valid && st.valid) {
    startDT = sd.moment
      .clone()
      .set({ hour: st.moment.hours(), minute: st.moment.minutes(), second: 0 })
  }
  if (ed.valid && et.valid) {
    endDT = ed.moment
      .clone()
      .set({ hour: et.moment.hours(), minute: et.moment.minutes(), second: 0 })
  }
  if (!result && startDT && endDT && !endDT.isAfter(startDT)) result = 'invalid_range'

  if (
    !result &&
    startDT &&
    !roundWindows.some((w) => !startDT.isBefore(w.start) && !startDT.isAfter(w.end))
  ) {
    result = 'no_round'
  }

  const team1Match = teamBySn.get(team1.toUpperCase())
  const team2Match = mode === 'dual' ? teamBySn.get(team2.toUpperCase()) : null

  return {
    team1,
    team1_name: team1Match ? team1Match.name : '',
    team2,
    team2_name: team2Match ? team2Match.name : '',
    match_type: matchType,
    start_date: sd.text,
    start_time: st.text,
    end_date: ed.text,
    end_time: et.text,
    startDT,
    endDT,
    result,
  }
}

const buildValidatedRows = (baseRows, ctx) =>
  baseRows.map((base, idx) => {
    const r = buildRow(base, ctx)
    const result = r.result || 'valid'
    return { row: idx + 1, ...r, result, included: result === 'valid' }
  })

const MiniStepper = ({ current, maxReached, onStepClick }) => (
  <div className="d-flex align-items-center mb-4 flex-wrap">
    {WIZARD_STEPS.map((label, idx) => {
      const n = idx + 1
      const done = n < current
      const active = n === current
      const clickable = n <= maxReached
      return (
        <React.Fragment key={label}>
          <div
            className="d-flex align-items-center gap-2"
            style={{ cursor: clickable ? 'pointer' : 'default', opacity: clickable ? 1 : 0.5 }}
            onClick={() => clickable && onStepClick(n)}
          >
            <div
              className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${
                done ? 'bg-success text-white' : active ? 'bg-primary text-white' : 'bg-body border'
              }`}
              style={{ width: 28, height: 28, fontSize: 13 }}
            >
              {done ? <CIcon icon={cilCheckAlt} size="sm" /> : n}
            </div>
            <span className={`small ${active ? 'fw-semibold text-primary' : ''}`}>{label}</span>
          </div>
          {n < WIZARD_STEPS.length && (
            <div
              className={`mx-2 ${done ? 'bg-success' : 'bg-secondary-subtle'}`}
              style={{ height: 2, width: 32 }}
            />
          )}
        </React.Fragment>
      )
    })}
  </div>
)

const InfoBox = ({ children, color = 'info' }) => (
  <CAlert color={color} className="d-flex align-items-start gap-2 border-0">
    <CIcon icon={cilInfo} className="flex-shrink-0 mt-1" />
    <div>{children}</div>
  </CAlert>
)

const TONE_HEX = {
  success: '#198754',
  danger: '#dc3545',
  warning: '#b45309',
  info: '#087990',
  secondary: '#495057',
}

const StatTile = ({ label, value, tone, icon }) => {
  const hex = TONE_HEX[tone] || TONE_HEX.secondary
  return (
    <div
      className="rounded-4 p-3 flex-fill d-flex align-items-center gap-3"
      style={{ ...tintStyle(hex, '14'), border: `1px solid ${hex}33` }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
        style={{ width: 38, height: 38, backgroundColor: hex, color: '#fff' }}
      >
        <CIcon icon={icon} size="sm" />
      </div>
      <div>
        <div className="fs-4 fw-bold lh-1" style={{ color: hex }}>
          {value}
        </div>
        <div className="small text-medium-emphasis">{label}</div>
      </div>
    </div>
  )
}

// Fixture Import: upload a CSV/Excel file, validate each row against this game's actual teams
// and its rounds' lockout windows (fetched, not hardcoded), fix any of it inline, then commit
// only the rows left included. Mirrors the Player/Round Import wizards' 3-step shape (Upload
// File -> Validate -> Review & Import). The row shape - and which import endpoint gets called -
// depends on game_type: a club's own fixtures (type 1) list a single team_code against an
// external opponent, everything else lists two team codes from this game's own team list.
const ImportFixture = () => {
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const [gameType, setGameType] = useState(null)
  const mode = gameType === 1 ? 'single' : 'dual'

  const [wizardStep, setWizardStep] = useState(1)
  const [maxWizardStep, setMaxWizardStep] = useState(1)

  const [fileName, setFileName] = useState('')
  const [fileRowCount, setFileRowCount] = useState(0)
  const [busy, setBusy] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  const [optionsLoading, setOptionsLoading] = useState(true)
  const [teams, setTeams] = useState([]) // { id, name, sn, image }
  const [matchTypeOptions, setMatchTypeOptions] = useState(FALLBACK_MATCH_TYPES)
  const [roundWindows, setRoundWindows] = useState([]) // { start: moment, end: moment }

  const [sourceRows, setSourceRows] = useState([]) // the file's values (inline edits applied), re-read whenever dateOrder changes
  const [dateOrder, setDateOrder] = useState('dmy')
  const [dateOrderInfo, setDateOrderInfo] = useState({ source: 'default', hasNumeric: false })
  const [rows, setRows] = useState([]) // { row, team1, team2, match_type, start_date, start_time, end_date, end_time, result, included }
  const [editingRow, setEditingRow] = useState(null)
  const [editDraft, setEditDraft] = useState({
    team1: '',
    team2: '',
    match_type: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
  })

  const [importing, setImporting] = useState(false)
  const [importErrors, setImportErrors] = useState([])

  useEffect(() => {
    CommonService.getGameType().then((res) => {
      if (res.status === 200) setGameType(res.data)
    })
  }, [])

  useEffect(() => {
    if (gameType === null) return
    setOptionsLoading(true)
    Promise.all([
      TeamService.getTeamListByGrade(),
      RoundService.getRound(0, 1000, 1, new URLSearchParams()),
    ])
      .then(([teamRes, roundRes]) => {
        if (teamRes?.status === 200) {
          setTeams(teamRes.data || [])
          if (teamRes.matchTypeList?.length) {
            setMatchTypeOptions(teamRes.matchTypeList.map((m) => m.name))
          }
        }
        const rounds = roundRes?.data?.data || []
        setRoundWindows(
          rounds
            .filter((rd) => rd.start_date && rd.end_date)
            .map((rd) => ({ start: moment(rd.start_date), end: moment(rd.end_date) })),
        )
      })
      .catch(() => ToastComponent('Could not load teams/round data for this game.', 'error'))
      .finally(() => setOptionsLoading(false))
  }, [gameType])

  const teamBySn = useMemo(() => {
    const map = new Map()
    teams.forEach((t) => t.sn && map.set(String(t.sn).toUpperCase(), t))
    return map
  }, [teams])

  const validationCtx = useMemo(
    () => ({ mode, teamBySn, roundWindows, dateOrder }),
    [mode, teamBySn, roundWindows, dateOrder],
  )

  // Re-validates every row from its source values. A row that was valid before and still is
  // keeps its include toggle; the row just edited (if any) starts fresh.
  const revalidate = (nextSource, ctx, editedRow = null) =>
    buildValidatedRows(nextSource, ctx).map((r, idx) => {
      const prevRow = rows[idx]
      if (r.row === editedRow) return r
      return r.result === 'valid' && prevRow?.result === 'valid'
        ? { ...r, included: prevRow.included }
        : r
    })

  const handleDateOrderChange = (order) => {
    setDateOrder(order)
    setRows(revalidate(sourceRows, { ...validationCtx, dateOrder: order }))
  }

  const goToStep = (n) => {
    setWizardStep(n)
    setMaxWizardStep((m) => Math.max(m, n))
  }

  const resetAll = () => {
    setWizardStep(1)
    setMaxWizardStep(1)
    setFileName('')
    setFileRowCount(0)
    setSourceRows([])
    setDateOrder('dmy')
    setDateOrderInfo({ source: 'default', hasNumeric: false })
    setRows([])
    setImportErrors([])
  }

  const handleFile = (file) => {
    if (!file) return
    if (file.size > MAX_FILE_SIZE) {
      ToastComponent('File is too large. Maximum size is 10MB.', 'error')
      return
    }
    setBusy(true)
    const reader = new FileReader()
    reader.onload = (evt) => {
      let json = []
      let rawHeaders = []
      try {
        // CSV cells are read as plain text: letting SheetJS guess dates reads "9/12/2026" as
        // US month/day while "13/12/2026" stays text, so one file ends up in two orders.
        // Spreadsheet date cells come through as serials and are converted below.
        const isCsv = /\.csv$/i.test(file.name)
        const workbook = XLSX.read(
          evt.target.result,
          isCsv ? { type: 'string', raw: true } : { type: 'array' },
        )
        const sheet = workbook.Sheets[workbook.SheetNames[0]]
        json = XLSX.utils.sheet_to_json(sheet, { defval: '' })
        rawHeaders = json.length ? Object.keys(json[0]) : []
      } catch (err) {
        ToastComponent('Could not read this file. Please check the format and try again.', 'error')
        setBusy(false)
        return
      }

      if (!rawHeaders.length) {
        ToastComponent('This file has no rows to import.', 'error')
        setBusy(false)
        return
      }

      const detected = {
        team1:
          mode === 'single'
            ? guessColumn(rawHeaders, ['teamcode', 'team'])
            : guessColumn(rawHeaders, ['team1code', 'teamonecode', 'hometeamcode', 'team1']),
        team2: guessColumn(rawHeaders, ['team2code', 'teamtwocode', 'awayteamcode', 'team2']),
        match_type: guessColumn(rawHeaders, ['matchtype', 'type']),
        start_date: guessColumn(rawHeaders, ['startdate', 'datestart', 'matchdate']),
        start_time: guessColumn(rawHeaders, ['starttime', 'timestart']),
        end_date: guessColumn(rawHeaders, ['enddate', 'dateend']),
        end_time: guessColumn(rawHeaders, ['endtime', 'timeend']),
      }

      const cell = (r, key, kind) => {
        const value = detected[key] ? r[detected[key]] : ''
        return kind && typeof value === 'number' ? fromExcelSerial(value, kind) : value
      }
      const baseRows = json.map((r) => ({
        team1: cell(r, 'team1'),
        team2: cell(r, 'team2'),
        match_type: cell(r, 'match_type'),
        start_date: cell(r, 'start_date', 'date'),
        start_time: cell(r, 'start_time', 'time'),
        end_date: cell(r, 'end_date', 'date'),
        end_time: cell(r, 'end_time', 'time'),
      }))
      const detectedOrder = detectDateOrder(baseRows)

      setFileName(file.name)
      setFileRowCount(json.length)
      setSourceRows(baseRows)
      setDateOrder(detectedOrder.order)
      setDateOrderInfo({ source: detectedOrder.source, hasNumeric: detectedOrder.hasNumeric })
      setRows(buildValidatedRows(baseRows, { ...validationCtx, dateOrder: detectedOrder.order }))
      setBusy(false)
    }
    if (/\.csv$/i.test(file.name)) reader.readAsText(file)
    else reader.readAsArrayBuffer(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    if (!optionsLoading) handleFile(e.dataTransfer.files?.[0])
  }

  const toggleRowIncluded = (row) => {
    setRows((prev) =>
      prev.map((r) =>
        r.row === row && r.result === 'valid' ? { ...r, included: !r.included } : r,
      ),
    )
  }

  const startEdit = (r) => {
    setEditingRow(r.row)
    setEditDraft({
      team1: r.team1,
      team2: r.team2,
      match_type: r.match_type,
      start_date: r.start_date,
      start_time: r.start_time,
      end_date: r.end_date,
      end_time: r.end_time,
    })
  }

  const cancelEdit = () => {
    setEditingRow(null)
    setEditDraft({
      team1: '',
      team2: '',
      match_type: '',
      start_date: '',
      start_time: '',
      end_date: '',
      end_time: '',
    })
  }

  const saveEdit = (row) => {
    const draft = {
      team1: editDraft.team1.trim(),
      team2: editDraft.team2.trim(),
      match_type: editDraft.match_type.trim(),
      start_date: editDraft.start_date.trim(),
      start_time: editDraft.start_time.trim(),
      end_date: editDraft.end_date.trim(),
      end_time: editDraft.end_time.trim(),
    }
    // The edit inputs hand back "YYYY-MM-DD" / "HH:mm", which read the same in either date order.
    const nextSource = sourceRows.map((base, idx) => (idx + 1 === row ? draft : base))
    setSourceRows(nextSource)
    setRows(revalidate(nextSource, validationCtx, row))
    cancelEdit()
  }

  const summary = useMemo(() => {
    const valid = rows.filter((r) => r.result === 'valid').length
    return { total: rows.length, valid, needsAttention: rows.length - valid }
  }, [rows])

  const readyRows = rows.filter((r) => r.included)

  // Rows are already canonical "YYYY-MM-DD" / "HH:mm", so they go over as-is - no day/month
  // order for the backend to guess at.
  const buildPayloadRow = (r) => {
    const base = {
      match_type: r.match_type,
      start_date: r.start_date,
      end_date: r.end_date,
      start_time: r.start_time,
      end_time: r.end_time,
    }
    return mode === 'single'
      ? { ...base, team_code: r.team1 }
      : { ...base, team1_code: r.team1, team2_code: r.team2 }
  }

  const runImport = () => {
    if (!readyRows.length) return
    setImporting(true)
    setImportErrors([])
    const fixtureData = readyRows.map(buildPayloadRow)
    const call =
      gameType === 1 ? FixtureService.fixtureImport : FixtureService.importFixtureForIndoor
    call({ fixtureData })
      .then((res) => {
        if (res.success) {
          ToastComponent(res.message || 'Fixtures imported successfully.', 'success')
          resetAll()
          navigate('/create-fixtures')
        } else {
          const errors = Array.isArray(res.errors)
            ? res.errors
            : res.errors
            ? Object.values(res.errors).flat()
            : [res.message || 'Import failed. Please check your rows.']
          setImportErrors(errors)
          ToastComponent(res.message || 'Import failed. Please check the errors below.', 'error')
        }
      })
      .catch(() => ToastComponent('Import failed. Please try again.', 'error'))
      .finally(() => setImporting(false))
  }

  const sampleCsv = mode === 'single' ? ExampleSingle : ExampleDual

  const renderUpload = () => (
    <>
      <CRow className="g-4">
        <CCol lg={5}>
          <CCard className="border-secondary-subtle rounded-4 h-100">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Download Template</div>
              <div className="small mb-3">Use the template to prepare your fixture records.</div>
              <a
                href={sampleCsv}
                download="Fixture-Sample-CSV.csv"
                target="_blank"
                rel="noreferrer"
              >
                <CButton color="primary" variant="outline" className="mb-4">
                  <CIcon icon={cilCloudDownload} className="me-1" />
                  Download Fixture Template
                </CButton>
              </a>
              <div className="fw-semibold mb-2">Required columns</div>
              <ul className="ps-3 mb-3 small">
                {mode === 'single' ? (
                  <li>Team Code</li>
                ) : (
                  <>
                    <li>Team 1 Code</li>
                    <li>Team 2 Code</li>
                  </>
                )}
                <li>
                  Match Type <span className="text-medium-emphasis">(1-Day, 2-Day)</span>
                </li>
                <li>Start Date</li>
                <li>Start Time</li>
                <li>End Date</li>
                <li>End Time</li>
              </ul>
              <InfoBox color="light">
                Team code can be found next to the team name in the Teams section.{' '}
                {mode === 'single'
                  ? 'Dates like D.M.YYYY (e.g. 5.9.2025), times like h:mm AM/PM (e.g. 4:40 PM).'
                  : 'Dates like D.MM.YYYY (e.g. 5.09.2025), times like h.mm AM/PM (e.g. 4.40 PM).'}{' '}
                Each fixture&rsquo;s start date/time must fall within an existing round&rsquo;s
                lockout window.
              </InfoBox>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={7}>
          <CCard className="border-secondary-subtle rounded-4 h-100">
            <CCardBody>
              <div className="fw-semibold fs-6 mb-1">Upload Completed Spreadsheet</div>
              <div className="small mb-3">
                Drag and drop an XLSX or CSV file, or browse your computer.
              </div>

              {!fileName ? (
                <div
                  className="border rounded-4 d-flex flex-column align-items-center justify-content-center text-center p-4"
                  style={{
                    cursor: optionsLoading ? 'not-allowed' : 'pointer',
                    minHeight: 180,
                    backgroundColor: isDragging ? 'rgba(var(--cui-primary-rgb), 0.06)' : undefined,
                    borderStyle: 'dashed',
                    opacity: optionsLoading ? 0.6 : 1,
                  }}
                  onClick={() => !optionsLoading && fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault()
                    if (!optionsLoading) setIsDragging(true)
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                >
                  <CIcon icon={cilCloudUpload} size="xl" className="text-primary mb-2" />
                  <div>
                    Drag and drop an XLSX or CSV file, or{' '}
                    <span className="text-primary">browse</span>
                  </div>
                  <div className="text-medium-emphasis small mt-1">Maximum file size 10MB</div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".csv,.xlsx,.xls"
                    disabled={optionsLoading}
                    onChange={(e) => {
                      handleFile(e.target.files?.[0])
                      e.target.value = ''
                    }}
                  />
                </div>
              ) : (
                <CCard className="border-secondary-subtle rounded-3">
                  <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <CIcon icon={cilFile} className="text-success" size="xl" />
                      <div>
                        <div className="fw-semibold">{fileName}</div>
                        <div className="small text-success d-flex align-items-center gap-1">
                          <CIcon icon={cilCheckCircle} size="sm" />
                          {fileRowCount} row{fileRowCount === 1 ? '' : 's'} detected
                        </div>
                      </div>
                    </div>
                    <CButton color="link" className="text-danger p-0" onClick={resetAll}>
                      Remove
                    </CButton>
                  </CCardBody>
                </CCard>
              )}

              {(busy || optionsLoading) && (
                <div className="d-flex align-items-center gap-2 small text-medium-emphasis mt-2">
                  <CSpinner size="sm" />
                  {optionsLoading
                    ? 'Loading teams and round data for this game…'
                    : 'Reading and validating your file…'}
                </div>
              )}

              <div className="text-medium-emphasis small mt-2">
                <a
                  href={sampleCsv}
                  download="Fixture-Sample-CSV.csv"
                  target="_blank"
                  rel="noreferrer"
                  className="text-medium-emphasis text-decoration-none"
                >
                  <CIcon icon={cilCloudDownload} size="sm" className="me-1" />
                  Download Sample CSV
                </a>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <div className="d-flex justify-content-between mt-4">
        <CButton color="secondary" variant="outline" onClick={() => navigate('/create-fixtures')}>
          Cancel
        </CButton>
        <CButton color="primary" disabled={!rows.length} onClick={() => goToStep(2)}>
          Review File
        </CButton>
      </div>
    </>
  )

  const renderValidate = () => (
    <>
      <CCard className="border-secondary-subtle rounded-4 mb-3">
        <CCardBody className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <CIcon icon={cilFile} className="text-primary" />
            <div>
              <div className="fw-semibold">{fileName}</div>
              <div className="small text-medium-emphasis">{fileRowCount} rows</div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {dateOrderInfo.hasNumeric && (
              <div className="d-flex align-items-center gap-2">
                <label htmlFor="date_order" className="small text-medium-emphasis text-nowrap">
                  Dates in file
                </label>
                <CFormSelect
                  id="date_order"
                  size="sm"
                  value={dateOrder}
                  onChange={(e) => handleDateOrderChange(e.target.value)}
                  style={{ width: 'auto' }}
                >
                  {Object.entries(DATE_ORDER_LABELS).map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </CFormSelect>
              </div>
            )}
            <CButton color="secondary" variant="outline" size="sm" onClick={resetAll}>
              Replace File
            </CButton>
          </div>
        </CCardBody>
      </CCard>

      {dateOrderInfo.hasNumeric && dateOrderInfo.source !== 'detected' && (
        <InfoBox color="warning">
          {dateOrderInfo.source === 'mixed'
            ? 'This file mixes day-first and month-first dates, so some rows may be flagged as invalid. '
            : 'We could not tell from this file whether dates are day-first or month-first (e.g. 9/12/2026). '}
          They are being read as <strong>{DATE_ORDER_LABELS[dateOrder]}</strong> — check the dates
          below and change &quot;Dates in file&quot; if they look swapped.
        </InfoBox>
      )}

      <div className="d-flex gap-3 flex-wrap mb-3">
        <StatTile label="Valid" value={summary.valid} tone="success" icon={cilCheckCircle} />
        <StatTile
          label="Needs Attention"
          value={summary.needsAttention}
          tone="warning"
          icon={cilWarning}
        />
      </div>

      <CCard className="border-secondary-subtle rounded-4 overflow-hidden">
        <div className="table-responsive">
          <CTable align="middle" hover className="mb-0 pi-import-table">
            <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
              <CTableRow>
                <CTableHeaderCell className="ps-4">Row</CTableHeaderCell>
                <CTableHeaderCell>{mode === 'single' ? 'Team' : 'Team 1'}</CTableHeaderCell>
                {mode === 'dual' && <CTableHeaderCell>Team 2</CTableHeaderCell>}
                <CTableHeaderCell>Match Type</CTableHeaderCell>
                <CTableHeaderCell>Start Date</CTableHeaderCell>
                <CTableHeaderCell>Start Time</CTableHeaderCell>
                <CTableHeaderCell>End Date</CTableHeaderCell>
                <CTableHeaderCell>End Time</CTableHeaderCell>
                <CTableHeaderCell>Result</CTableHeaderCell>
                <CTableHeaderCell>Action</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {rows.map((r) => {
                const meta = RESULT_META[r.result] || RESULT_META.valid
                const label = typeof meta.label === 'function' ? meta.label(mode) : meta.label
                const isEditing = editingRow === r.row
                return (
                  <CTableRow
                    key={r.row}
                    style={{
                      ...tintStyle(meta.accent, '0d'),
                      borderLeft: `3px solid ${meta.accent}`,
                    }}
                  >
                    <CTableDataCell className="ps-4">{r.row}</CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormSelect
                          size="sm"
                          value={editDraft.team1}
                          onChange={(e) => setEditDraft((d) => ({ ...d, team1: e.target.value }))}
                        >
                          <option value="">Select Team</option>
                          {teams.map((t) => (
                            <option value={t.sn} key={t.id}>
                              {t.name} ({t.sn})
                            </option>
                          ))}
                        </CFormSelect>
                      ) : r.team1 ? (
                        `${r.team1_name || r.team1}${r.team1_name ? ` (${r.team1})` : ''}`
                      ) : (
                        '—'
                      )}
                    </CTableDataCell>
                    {mode === 'dual' && (
                      <CTableDataCell>
                        {isEditing ? (
                          <CFormSelect
                            size="sm"
                            value={editDraft.team2}
                            onChange={(e) => setEditDraft((d) => ({ ...d, team2: e.target.value }))}
                          >
                            <option value="">Select Team</option>
                            {teams.map((t) => (
                              <option value={t.sn} key={t.id}>
                                {t.name} ({t.sn})
                              </option>
                            ))}
                          </CFormSelect>
                        ) : r.team2 ? (
                          `${r.team2_name || r.team2}${r.team2_name ? ` (${r.team2})` : ''}`
                        ) : (
                          '—'
                        )}
                      </CTableDataCell>
                    )}
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormSelect
                          size="sm"
                          value={editDraft.match_type}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, match_type: e.target.value }))
                          }
                        >
                          <option value="">Select Match Type</option>
                          {matchTypeOptions.map((mt) => (
                            <option value={mt} key={mt}>
                              {mt}
                            </option>
                          ))}
                        </CFormSelect>
                      ) : (
                        r.match_type || '—'
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          type="date"
                          size="sm"
                          value={editDraft.start_date}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, start_date: e.target.value }))
                          }
                        />
                      ) : (
                        displayDate(r.start_date)
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          type="time"
                          size="sm"
                          value={editDraft.start_time}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, start_time: e.target.value }))
                          }
                        />
                      ) : (
                        displayTime(r.start_time)
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          type="date"
                          size="sm"
                          value={editDraft.end_date}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, end_date: e.target.value }))
                          }
                        />
                      ) : (
                        displayDate(r.end_date)
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <CFormInput
                          type="time"
                          size="sm"
                          value={editDraft.end_time}
                          onChange={(e) =>
                            setEditDraft((d) => ({ ...d, end_time: e.target.value }))
                          }
                        />
                      ) : (
                        displayTime(r.end_time)
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge
                        color={meta.color}
                        shape="rounded-pill"
                        className="d-inline-flex align-items-center gap-1 px-2 py-1"
                      >
                        <CIcon icon={meta.icon} size="sm" />
                        {label}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      {isEditing ? (
                        <div className="d-flex gap-1">
                          <CButton size="sm" color="primary" onClick={() => saveEdit(r.row)}>
                            <CIcon icon={cilCheck} />
                          </CButton>
                          <CButton
                            size="sm"
                            color="secondary"
                            variant="outline"
                            onClick={cancelEdit}
                          >
                            <CIcon icon={cilX} />
                          </CButton>
                        </div>
                      ) : (
                        <div className="d-flex gap-2">
                          <CButton
                            size="sm"
                            color="link"
                            className="p-0"
                            onClick={() => startEdit(r)}
                          >
                            Edit
                          </CButton>
                          {r.result === 'valid' && (
                            <CButton
                              size="sm"
                              color="link"
                              className="p-0"
                              onClick={() => toggleRowIncluded(r.row)}
                            >
                              {r.included ? 'Skip' : 'Include'}
                            </CButton>
                          )}
                        </div>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                )
              })}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      <div className="mt-3">
        <InfoBox>
          Rows that need correction are excluded from the import until fixed. Use{' '}
          <strong>Edit</strong> to fix a row inline, or <strong>Skip</strong> to leave a valid row
          out of this import.
        </InfoBox>
      </div>

      <div className="d-flex justify-content-between mt-3">
        <CButton color="secondary" variant="outline" onClick={() => goToStep(1)}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Upload
        </CButton>
        <CButton color="primary" disabled={!readyRows.length} onClick={() => goToStep(3)}>
          Continue to Review
        </CButton>
      </div>
    </>
  )

  const renderReview = () => {
    const excluded = rows.length - readyRows.length
    return (
      <>
        {importErrors.length > 0 && (
          <div className="mb-3">
            {importErrors.map((error, index) => (
              <CAlert color="danger" key={index}>
                {error}
              </CAlert>
            ))}
          </div>
        )}
        <CRow className="g-3 mb-3">
          <CCol sm={6} lg={3}>
            <StatTile label="File Rows" value={rows.length} tone="secondary" icon={cilFile} />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile
              label="Ready to Import"
              value={readyRows.length}
              tone="success"
              icon={cilCheckCircle}
            />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile label="Excluded" value={excluded} tone="warning" icon={cilWarning} />
          </CCol>
          <CCol sm={6} lg={3}>
            <StatTile
              label="Needs Correction"
              value={summary.needsAttention}
              tone="danger"
              icon={cilWarning}
            />
          </CCol>
        </CRow>
        <InfoBox>
          This will create {readyRows.length} new fixture{readyRows.length === 1 ? '' : 's'}. If any
          row is rejected by the server, the whole import may be rejected — review the list below
          before importing.
        </InfoBox>
        <CCard className="border-secondary-subtle rounded-4 overflow-hidden mt-3">
          <div className="table-responsive" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <CTable align="middle" hover className="mb-0 pi-import-table">
              <CTableHead style={{ backgroundColor: 'var(--cui-tertiary-bg, #f8f9fa)' }}>
                <CTableRow>
                  <CTableHeaderCell className="ps-4">
                    {mode === 'single' ? 'Team' : 'Team 1'}
                  </CTableHeaderCell>
                  {mode === 'dual' && <CTableHeaderCell>Team 2</CTableHeaderCell>}
                  <CTableHeaderCell>Match Type</CTableHeaderCell>
                  <CTableHeaderCell>Start Date</CTableHeaderCell>
                  <CTableHeaderCell>Start Time</CTableHeaderCell>
                  <CTableHeaderCell>End Date</CTableHeaderCell>
                  <CTableHeaderCell>End Time</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {readyRows.map((r) => (
                  <CTableRow key={r.row}>
                    <CTableDataCell className="ps-4">{r.team1_name || r.team1}</CTableDataCell>
                    {mode === 'dual' && <CTableDataCell>{r.team2_name || r.team2}</CTableDataCell>}
                    <CTableDataCell>{r.match_type}</CTableDataCell>
                    <CTableDataCell>{displayDate(r.start_date)}</CTableDataCell>
                    <CTableDataCell>{displayTime(r.start_time)}</CTableDataCell>
                    <CTableDataCell>{displayDate(r.end_date)}</CTableDataCell>
                    <CTableDataCell>{displayTime(r.end_time)}</CTableDataCell>
                  </CTableRow>
                ))}
              </CTableBody>
            </CTable>
          </div>
        </CCard>
        <div className="d-flex justify-content-between mt-3">
          <CButton color="secondary" variant="outline" onClick={() => goToStep(2)}>
            <CIcon icon={cilArrowLeft} className="me-1" />
            Back
          </CButton>
          <CLoadingButton
            color="success"
            loading={importing}
            disabled={!readyRows.length}
            onClick={runImport}
          >
            <CIcon icon={cilCloudUpload} className="me-1" />
            Import {readyRows.length} Fixture{readyRows.length === 1 ? '' : 's'}
          </CLoadingButton>
        </div>
      </>
    )
  }

  return (
    <>
      <style>{`
        .pi-import-table > :not(caption) > * > * { padding-top: .85rem; padding-bottom: .85rem; }
      `}</style>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h4 className="fw-bold mb-0">Import Fixtures</h4>
          <div className="small text-medium-emphasis">
            Create multiple fixture records from a spreadsheet.
          </div>
        </div>
        <CButton color="secondary" variant="outline" onClick={() => navigate('/create-fixtures')}>
          <CIcon icon={cilArrowLeft} className="me-1" />
          Back to Fixtures
        </CButton>
      </div>
      <CCard className="mb-4 border-0 shadow-sm">
        <CCardBody>
          {gameType === null ? (
            <div className="d-flex align-items-center gap-2 text-medium-emphasis py-4 justify-content-center">
              <CSpinner size="sm" />
              Loading game settings…
            </div>
          ) : (
            <>
              <MiniStepper current={wizardStep} maxReached={maxWizardStep} onStepClick={goToStep} />
              {wizardStep === 1 && renderUpload()}
              {wizardStep === 2 && renderValidate()}
              {wizardStep === 3 && renderReview()}
            </>
          )}
        </CCardBody>
      </CCard>
    </>
  )
}

export default ImportFixture
